import { useEffect, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { X } from 'lucide-react';
import { animate, motion, useMotionValue, type PanInfo } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { hapticTick } from '@/lib/haptics';

// Swipe-down dismiss thresholds for the mobile sheet.
const SHEET_DISMISS_DISTANCE = 120;
const SHEET_DISMISS_VELOCITY = 600;

export interface CreationPopoverVirtualAnchor {
  getBoundingClientRect: () => DOMRect;
  contextElement?: Element;
}

interface CreationPopoverProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: ReactNode;
  /** Extra header controls (e.g. a refresh button), rendered left of Close. */
  headerActions?: ReactNode;
  anchorRef?: RefObject<CreationPopoverVirtualAnchor | null>;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Desktop width classes; mobile always renders the full-height sheet. */
  desktopClassName?: string;
  desktopStyle?: CSSProperties;
  children: ReactNode;
}

/**
 * Shared shell for the email creation surfaces (task / event / product line).
 * Desktop: popover anchored to the launching control. Mobile: a bottom sheet
 * that fills the screen below the status bar, so placement and size are
 * identical regardless of content. Both variants share a header with the
 * title and an explicit close button.
 */
export function CreationPopover({
  open,
  onClose,
  title,
  icon,
  headerActions,
  anchorRef,
  align,
  side,
  desktopClassName,
  desktopStyle,
  children,
}: CreationPopoverProps) {
  const isMobile = useIsMobile();

  // Swipe-down-to-close: the sheet follows the finger 1:1 (drag surface =
  // grab handle + header; the body must stay free for scrolling) and commits
  // past a distance or flick-velocity threshold.
  const sheetY = useMotionValue(0);

  useEffect(() => {
    if (open) {
      sheetY.set(0);
    }
  }, [open, sheetY]);

  const handleSheetPan = (_event: PointerEvent, info: PanInfo) => {
    sheetY.set(Math.max(0, info.offset.y));
  };

  const handleSheetPanEnd = (_event: PointerEvent, info: PanInfo) => {
    if (info.offset.y > SHEET_DISMISS_DISTANCE || info.velocity.y > SHEET_DISMISS_VELOCITY) {
      hapticTick(10);
      onClose();
    } else {
      animate(sheetY, 0, { type: 'spring', stiffness: 500, damping: 42 });
    }
  };

  const header = (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
      <div className="flex min-w-0 items-center gap-2">
        {icon}
        <span className="truncate text-sm font-semibold">{title}</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {headerActions}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const body = <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>;

  if (isMobile) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            onClose();
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className={cn(
            // Transparent positioned shell: the visible sheet is the inner
            // motion.div so the swipe-down translation moves the whole surface.
            'flex flex-col gap-0 p-0 overflow-visible bg-transparent border-0 shadow-none rounded-none',
            // Full-height sheet: pinned to the bottom, top edge just below the
            // status bar — overrides the default centered-dialog transforms.
            // max-h-none: the base DialogContent max-h clamp would un-anchor
            // the bottom edge; the top calc already respects the notch inset.
            '!fixed !inset-x-0 !bottom-0 !left-0 !top-[calc(env(safe-area-inset-top,0px)+0.75rem)] !w-full !max-w-none !max-h-none !translate-x-0 !translate-y-0',
            'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
          )}
          onEscapeKeyDown={(event) => event.stopPropagation()}
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <motion.div
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-t-2xl bg-background shadow-lg"
            style={{ y: sheetY, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            <motion.div
              className="touch-none select-none"
              onPan={handleSheetPan}
              onPanEnd={handleSheetPanEnd}
            >
              <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-muted-foreground/25" />
              {header}
            </motion.div>
            {body}
          </motion.div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <PopoverAnchor
        virtualRef={anchorRef?.current ? (anchorRef as RefObject<CreationPopoverVirtualAnchor>) : undefined}
      />
      <PopoverContent
        align={align}
        side={side}
        className={cn(
          'flex max-h-[80vh] flex-col overflow-hidden p-0',
          desktopClassName ?? '!w-[95vw] max-w-[720px]',
        )}
        style={desktopStyle}
        onEscapeKeyDown={(event) => event.stopPropagation()}
        onInteractOutside={onClose}
      >
        {header}
        {body}
      </PopoverContent>
    </Popover>
  );
}
